import moment, { Moment } from "moment";
import {
  countryCallingCodes,
  DEFAULT_LOCALE,
  ILocale,
} from "./constants/locale";
import { getLocaleFormat } from "./constants/localeFormatting";
import {
  getEnabledLocalesFromCookie,
  isEnabledLocale,
} from "./locales/enabledLocalesCookie";
import { PUBLIC_ROUTES } from "./constants/publicRoutes";
import JSZip from "jszip";
import { useState, useEffect, useRef } from "react";
import {
  IActionItem,
  IActionReminder,
  IAsset,
  IAssessment,
  IBroadcast,
  ICarePlanAction,
  ICarePlanTemplate,
  IEvent,
} from "./components/testData/carePlanData";
import { GroupedBroadcasts } from "@/app/[locale]/provider/patientpath/enrollments/[enrollmentId]/plan/page";
import { IDropdownOption } from "./components/Dropdown/Dropdown";
import FileIcon from "@/assets/icons/FileIcon.svg";
import ImageAssetIcon from "@/assets/icons/ImageAsset.svg";
import MarkdownAssetIcon from "@/assets/icons/MarkdownAsset.svg";
import VideoAssetIcon from "@/assets/icons/VideoAsset.svg";
import type { WorkflowRow } from "@/lib/analytics/fetchData";
import axios from "axios";
import {
  CognitoIdentityProviderClient,
  ListWebAuthnCredentialsCommand,
  WebAuthnCredentialDescription,
} from "@aws-sdk/client-cognito-identity-provider";
import config from "@/config";
import {
  BespokeTemplate,
  PredefinedAction,
  Reminder,
  UpdatePatientEnrollmentRequest,
} from "@clients/velys-infrastructure/api";
import { acceptedCountryCodes } from "./constants/acceptedOTPCodes";

export interface EnrollmentItemValidationTranslations {
  titleRequired: string;
  broadcastTypeRequired: string;
  daysPrePostRequired: string;
  reminderRequired: string;
  reminderTimeRequired: (index: number) => string;
  reminderDaysRequired: (index: number) => string;
  reminderMessageRequired: (index: number) => string;
  broadcastAssetRequired: string;
  messageContentRequired: string;
  pastDate: string;
}

export interface PredefinedActionValidationTranslations {
  titleRequired: string;
  daysPrePostRequired: string;
  reminderTimeRequired: (index: number) => string;
  reminderDaysRequired: (index: number) => string;
  reminderMessageRequired: (index: number) => string;
}

type ActionItemWithType = IActionItem & { itemType: "action" };
type EventWithType = IEvent & { itemType: "event" };
type CareplanItem = ActionItemWithType | EventWithType;

export const formatPhoneNumber = (value: string, locale: ILocale = "en-US") => {
  let digits = value.replace(/\D/g, ""); // only digits

  switch (locale) {
    case "en-US":
      digits = digits.slice(0, 10);
      if (digits.length === 0) return "";
      if (digits.length < 4) return digits;
      if (digits.length < 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;

    case "en-GB":
      digits = digits.slice(0, 10);
      if (digits.length === 0) return "";
      if (digits.length <= 5) return digits;
      return `${digits.slice(0, digits.length - 6)} ${digits.slice(
        digits.length - 6,
        digits.length - 3,
      )} ${digits.slice(digits.length - 3)}`;

    case "en-AT":
      // Austrian mobile: 06XX XXX XXXX (up to 13 digits)
      digits = digits.slice(0, 13);
      if (digits.length === 0) return "";
      if (digits.length <= 3) return digits;
      if (digits.length <= 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
      return `${digits.slice(0, 3)} ${digits.slice(3, 7)} ${digits.slice(7)}`;

    case "en-CH":
      // Swiss format: 0XX XXX XX XX (up to 10 digits)
      digits = digits.slice(0, 10);
      if (digits.length === 0) return "";
      if (digits.length <= 3) return digits;
      if (digits.length <= 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
      if (digits.length <= 8)
        return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6, 8)} ${digits.slice(8)}`;

    case "en-AU":
      digits = digits.slice(0, 10);
      if (digits.length === 0) return "";
      if (digits.startsWith("0")) digits = digits.slice(1); // strip leading zero if present
      if (digits.length < 3) return digits;
      if (digits.length < 6) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;

    case "es-ES":
      digits = digits.slice(0, 9);
      return digits.replace(/(\d{3})(\d{3})(\d{0,3})/, (_, g1, g2, g3) =>
        g3 ? `${g1} ${g2} ${g3}` : `${g1} ${g2}`,
      );

    case "fr-FR":
      digits = digits.slice(0, 9);
      if (!digits.startsWith("0")) digits = "0" + digits;
      return digits.replace(
        /(\d)(\d{2})(\d{2})(\d{2})(\d{2})/,
        "$1 $2 $3 $4 $5",
      );

    case "de-DE":
      digits = digits.slice(0, 11);
      if (!digits.startsWith("0")) digits = "0" + digits;
      if (digits.length <= 4) return digits;
      return `${digits.slice(0, 3)} ${digits.slice(3)}`;

    case "ja-JP":
      digits = digits.slice(0, 10);
      if (!digits.startsWith("0")) digits = "0" + digits;
      if (digits.length === 10) {
        if (digits[1] === "3" || digits[1] === "6" || digits[1] === "9") {
          return `${digits.slice(0, 2)} ${digits.slice(2, 6)} ${digits.slice(6)}`;
        } else {
          return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
        }
      }
      return digits;

    default:
      digits = digits.slice(0, 10);
      if (digits.length === 0) return "";
      if (digits.length < 4) return digits;
      if (digits.length < 7) return `${digits.slice(0, 3)} ${digits.slice(3)}`;
      return `${digits.slice(0, 3)} ${digits.slice(3, 6)} ${digits.slice(6)}`;
  }
};

export const validateEmail = (email: string) => {
  const regex = /\S+@\S+\.\S+/;
  return regex.test(email);
};

export const getPatientAge = (dob: string) => {
  const dobMoment = moment(dob, "YYYY-MM-DD");
  const age = moment().diff(dobMoment, "years");
  return `${dobMoment.format("MMM DD, YYYY")} (${age} yrs)`;
};

export async function downloadImagesAsZip(
  imageUrls: string[],
  zipFilename: string,
) {
  const zip = new JSZip();

  for (let i = 0; i < imageUrls.length; i++) {
    const url = imageUrls[i];
    try {
      const response = await fetch(url);
      if (!response.ok) throw new Error(`Failed to fetch ${url}`);
      const blob = await response.blob();

      // use same name decode on app
      const noQuery = url.split("?")[0];
      const segments = noQuery.split("/");
      const encodedFileName = segments[segments.length - 1];
      const decodedFileName = decodeURIComponent(encodedFileName);
      const nameWithoutExt = decodedFileName.replace(/\.[^/.]+$/, "");
      const originalName = nameWithoutExt
        .replace(/_/g, " ") // replace underscores with spaces
        .replace(/([A-Z])/g, " $1") // insert space before uppercase letters
        .trim()
        .replace(/\s+/g, " ");

      zip.file(originalName, blob);
    } catch (error) {
      console.error(`Error downloading ${url}:`, error);
    }
  }

  try {
    const zipBlob = await zip.generateAsync({ type: "blob" });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(zipBlob);
    link.download = zipFilename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);

    URL.revokeObjectURL(link.href);
  } catch (error) {
    console.error("Error generating zip:", error);
  }
}

export const getTimeDiffFormatted = (
  startTime: string | null | undefined,
  endTime: string | null | undefined,
) => {
  // Check for missing inputs
  if (!startTime || !endTime) return "00:00:00";

  const start = moment(startTime, "HH:mm:ss");
  const end = moment(endTime, "HH:mm:ss");

  // Calculate diff in seconds
  const diffSeconds = end.diff(start, "seconds");

  // Handle negative diffs if needed
  if (diffSeconds < 0) return "00:00:00";

  // Convert diff back to "HH:mm:ss"
  const time = moment.utc(diffSeconds * 1000).format("HH:mm:ss");
  return time;
};

export const formatDecimalMetrics = (metric: string | null | undefined) => {
  if (!metric) return "";
  if (metric.includes(".")) {
    return metric.split(".")[0];
  }
  return metric;
};

// ── Locale-driven formatting ──────────────────────────────────────────────────
// Per-locale rules live in the registry at ./constants/localeFormatting.
// Add a new country there, not here.

export {
  METRIC_24H_LOCALES,
  SWAPPED_NUM_LOCALES,
} from "./constants/localeFormatting";

/** Returns true for locales that display time in 24-hour format. */
export const is24HourLocale = (locale: string): boolean =>
  getLocaleFormat(locale).use24HourTime;

/** Returns true for locales that use European number separators (1.234,56). */
export const isEuropeanNumberLocale = (locale: string): boolean =>
  getLocaleFormat(locale).europeanNumberSeparators;

/** Returns true for locales that use metric measurement units. */
export const isMetricLocale = (locale: string): boolean =>
  getLocaleFormat(locale).metricUnits;

/**
 * Returns the moment.js date format for a locale, e.g. "MM/DD/YYYY" for en-US,
 * "DD.MM.YYYY" for German locales, "DD/MM/YYYY" otherwise.
 */
export const getDateFormatForLocale = (locale: string): string =>
  getLocaleFormat(locale).dateFormat;

/**
 * Formats a 24-hour time string ("14:30") for display.
 * EU locales → "14:30"; all others → "2:30 PM"
 */
export const formatTimeForLocale = (time24: string, locale: string): string => {
  if (!time24) return "";
  if (is24HourLocale(locale)) return time24;
  return moment(time24, "HH:mm").format("h:mm A");
};

/**
 * Formats a number with locale-appropriate separators.
 * en-AT → "1.234,56" | en-CH → "1'234.56" | all others → "1,234.56"
 */
export const formatNumberForLocale = (
  value: number | string,
  locale: string,
  decimals?: number,
): string => {
  const num = typeof value === "string" ? parseFloat(value) : value;
  if (isNaN(num)) return String(value);
  const intlLocale = getLocaleFormat(locale).numberLocale;
  return decimals !== undefined
    ? num.toLocaleString(intlLocale, {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
      })
    : num.toLocaleString(intlLocale);
};

// ── Measurement conversion utilities ─────────────────────────────────────────

/** Pounds → kilograms (1 decimal place). */
export const lbsToKg = (lbs: number): number =>
  Math.round(lbs * 0.453592 * 10) / 10;

/** Kilograms → pounds (1 decimal place). */
export const kgToLbs = (kg: number): number =>
  Math.round(kg * 2.20462 * 10) / 10;

/** Inches → centimetres (1 decimal place). */
export const inchesToCm = (inches: number): number =>
  Math.round(inches * 2.54 * 10) / 10;

/** Centimetres → inches (1 decimal place). */
export const cmToInches = (cm: number): number =>
  Math.round((cm / 2.54) * 10) / 10;

export const DebounceSearch = (value: string, delay: number) => {
  const [debounced, setDebounced] = useState(value);

  useEffect(() => {
    const handler = setTimeout(() => setDebounced(value), delay);
    return () => clearTimeout(handler);
  }, [value, delay]);

  return debounced;
};

export const getBrowserLanguage = (lang: string = "") => {
  if (!lang) return null;
  if (!lang.includes("-")) {
    // Bare language subtag ("de") — promote it to the first enabled locale in
    // that language. Reads the enabled list published by proxy.ts rather than
    // a build-time constant, so a language turned off in AppConfig stops
    // being a valid promotion target without a redeploy.
    const matchedLocale = getEnabledLocalesFromCookie().find((locale) =>
      locale.startsWith(lang),
    );
    return matchedLocale ?? null;
  }
  return lang;
};

export const sortAutoImages = (images: string[]) => {
  const autoImagesOrder = [
    "InitialLegAlignment",
    "TibiaSurgicalPlanning",
    "PostTibialLegAlignment",
    "FinalSurgicalPlanning",
    "FinalLegAlignment",
  ];

  const sortedImages = images.sort((a, b) => {
    const getKey = (filename: string) => {
      // get the part after "Automatic_"
      const parts = filename.split("Automatic_");
      return parts[1]?.split(".")[0] || "";
    };

    const indexA = autoImagesOrder.findIndex((key) => getKey(a) === key);
    const indexB = autoImagesOrder.findIndex((key) => getKey(b) === key);

    return (
      (indexA === -1 ? Infinity : indexA) - (indexB === -1 ? Infinity : indexB)
    );
  });
  return sortedImages;
};

export const toggleCheckboxDropdown = (
  currentSelected: string[],
  value: string,
  options: IDropdownOption[],
): string[] => {
  const allValues: string[] = options
    .filter((o) => o.value !== null && o.value !== "all")
    .map((o) => String(o.value));
  const isAllSelected = currentSelected.includes("all");

  if (value === "all") {
    return isAllSelected ? [] : ["all", ...allValues];
  }

  if (isAllSelected) {
    return allValues.filter((v) => v !== value);
  }

  const alreadySelected = currentSelected.includes(value);
  const newSelected = alreadySelected
    ? currentSelected.filter((v) => v !== value)
    : [...currentSelected, value];

  return newSelected.length === allValues.length
    ? ["all", ...allValues]
    : newSelected;
};

export const getDueDate = (surgeryDate: Moment, delta: string) =>
  moment(surgeryDate, "YYYY-MM-DD HH:mm:ss").add(parseInt(delta, 10), "days");

export const getRemindDate = (
  surgeryDate: Moment,
  dueDelta: string,
  remindDelta: string,
) => {
  const dueDate = getDueDate(surgeryDate, dueDelta);
  return dueDate.clone().subtract(parseInt(remindDelta, 10), "days");
};

export const generateId = () => {
  return Date.now().toString(36) + Math.random().toString(36).substring(2, 11);
};

export const weeksFromSurgery = (surgeryDate: Moment, uploadDate: string) => {
  const surgery = moment(surgeryDate);
  const uploaded = moment(uploadDate);

  const weeksDiff = Math.floor(uploaded.diff(surgery, "weeks", true));

  return weeksDiff;
};

export const groupBroadcasts = (
  broadcasts: IBroadcast[],
  selectedTimeframe: string,
) => {
  const isPreop = selectedTimeframe === "Pre-Op";
  const isPostop = selectedTimeframe === "Post-Op";
  const grouped: GroupedBroadcasts = {};

  broadcasts.forEach((b) => {
    const delta = Number(b.delta_days_from_surgery);

    // filter by timeframe
    if (isPreop && delta > 0) return;
    if (isPostop && delta <= 0) return;

    const weekKey = `${Math.floor(Math.abs(delta) / 7)}`;

    if (!grouped[weekKey]) grouped[weekKey] = [];
    grouped[weekKey].push(b);
  });

  return grouped;
};

// This function should filter by timeframe and sort
export const sortActionItems = (items: CareplanItem[], timeframe: string) => {
  const isPreop = timeframe === "Pre-Op";
  const isPostop = timeframe === "Post-Op";

  const filtered = items.filter((item) => {
    // Exception: Always show surgery event in both Pre-Op and Post-Op
    const isSurgeryEvent = item.itemType === "event" && item.event_date;
    if (isSurgeryEvent) return true;

    const delta = parseInt(item.delta_days_from_surgery, 10);
    if (isPreop && delta > 0) return false;
    if (isPostop && delta <= 0) return false;
    return true;
  });

  // Sort by delta_days_from_surgery
  return filtered.sort(
    (a, b) =>
      parseInt(a.delta_days_from_surgery, 10) -
      parseInt(b.delta_days_from_surgery, 10),
  );
};

export const isDatePastOrToday = (date: Moment) => {
  return date.isSameOrBefore(moment(), "day");
};

export const isDatePast = (date: Moment) => {
  return date.isBefore(moment(), "day");
};

export const validateEnrollmentItemFields = (
  selectedAction: ICarePlanAction,
  sendReminder: boolean,
  t: EnrollmentItemValidationTranslations,
  surgeryDate: string,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  const dueDate = selectedAction.delta_days_from_surgery
    ? getDueDate(moment(surgeryDate), selectedAction.delta_days_from_surgery)
    : moment();

  const isPastDate = isDatePast(dueDate);

  // Common required fields for all action types
  if (!selectedAction?.title?.trim()) {
    errors.push(t.titleRequired);
  }

  // is item due in the past
  if (isPastDate) {
    errors.push(t.pastDate);
  }

  // Broadcast-specific validation
  if (selectedAction?.type === "broadcast") {
    if (!selectedAction?.category) {
      errors.push(t.broadcastTypeRequired);
    }
  } else {
    // For non-broadcast items, delta_days_from_surgery is required
    if (
      !selectedAction?.delta_days_from_surgery &&
      selectedAction?.delta_days_from_surgery !== "0"
    ) {
      errors.push(t.daysPrePostRequired);
    }
  }

  // Reminder validation (only if sendReminder is enabled)
  if (sendReminder) {
    if (!selectedAction?.reminders?.length) {
      errors.push(t.reminderRequired);
    } else {
      selectedAction.reminders.forEach(
        (reminder: IActionReminder, index: number) => {
          if (!reminder.execution_date) {
            errors.push(t.reminderTimeRequired(index + 1));
          }
          if (!reminder.message?.trim()) {
            errors.push(t.reminderMessageRequired(index + 1));
          }
        },
      );
    }
  }

  /* 
    Broadcast asset validation (for broadcast type)
    New shape: assets[] is the source of truth; legacy `asset` is only read on
    open as a migration seed. At least one staged asset is required.
  */
  if (selectedAction?.type === "broadcast") {
    const assets = selectedAction?.assets ?? [];
    const legacyAsset = selectedAction?.asset;
    const hasAny = assets.length > 0 || !!legacyAsset;
    if (!hasAny) {
      errors.push(t.broadcastAssetRequired);
    } else {
      const toCheck =
        assets.length > 0 ? assets : legacyAsset ? [legacyAsset] : [];
      for (const a of toCheck) {
        const at = a.asset_type?.toLowerCase();
        if ((at === "markdown" || at === "message") && !a.description?.trim()) {
          errors.push(t.messageContentRequired);
          break;
        }
      }
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const createJsonFile = (
  data: BespokeTemplate,
  filename: string = "careplan.json",
): File => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  return new File([blob], filename, { type: "application/json" });
};

export const createEditJsonFile = (
  data: UpdatePatientEnrollmentRequest,
  filename: string = "editeCareplan.json",
): File => {
  const jsonString = JSON.stringify(data, null, 2);
  const blob = new Blob([jsonString], { type: "application/json" });
  return new File([blob], filename, { type: "application/json" });
};

export const fetchJsonData = async (s3Url: string) => {
  try {
    const response = await axios.get(s3Url, {
      headers: {
        "Cache-Control": "no-cache",
        Pragma: "no-cache",
      },
    });
    return response.data;
  } catch (error) {
    console.error("Failed to fetch from S3:", error);
    throw error;
  }
};

export const isSurgeryInPast = (surgeryDate: Moment) => {
  return moment().isAfter(surgeryDate, "day");
};

export const isJwtExpired = (token: string): boolean => {
  if (!token) return true;
  try {
    const payload = JSON.parse(atob(token.split(".")[1]));
    const now = Math.floor(Date.now() / 1000);

    return payload.exp <= now;
  } catch {
    return true;
  }
};

export const isMomentGeneratedId = (id: string): boolean => {
  return /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}\.\d{3}Z$/.test(id);
};

export const useInactivityTimer = (onTimeout: () => void) => {
  const timerRef = useRef<NodeJS.Timeout | null>(null);
  const minuteIntervalRef = useRef<NodeJS.Timeout | null>(null);

  const resetTimer = () => {
    if (timerRef.current) clearTimeout(timerRef.current);
    if (minuteIntervalRef.current) clearInterval(minuteIntervalRef.current);

    timerRef.current = setTimeout(() => {
      onTimeout();
    }, 900000);
    // 900000
  };

  useEffect(() => {
    const handleActivity = () => {
      resetTimer();
    };

    const events = [
      "mousemove",
      "mousedown",
      "keydown",
      "scroll",
      "touchstart",
      "touchmove",
    ];

    events.forEach((event) => window.addEventListener(event, handleActivity));

    resetTimer();

    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
      if (minuteIntervalRef.current) clearInterval(minuteIntervalRef.current);
      events.forEach((event) =>
        window.removeEventListener(event, handleActivity),
      );
    };
  }, [onTimeout]);
};

export async function listWebAuthnCredentials(
  accessToken: string,
): Promise<WebAuthnCredentialDescription[]> {
  const client = new CognitoIdentityProviderClient({
    region: config.cognito.region || "",
  });

  const command = new ListWebAuthnCredentialsCommand({
    AccessToken: accessToken,
  });

  const response = await client.send(command);
  return response.Credentials ?? [];
}

export async function userBiometric(accessToken: string) {
  try {
    const credentials = await listWebAuthnCredentials(accessToken);
    return credentials.length > 0;
  } catch (error) {
    console.error("Error checking WebAuthn credentials:", error);
    return false;
  }
}

/**
 * Best-effort device-type inference for the passkey list icon. Cognito does
 * not return attestation data, so this is a heuristic over the
 * FriendlyCredentialName / AuthenticatorAttachment / transports Cognito does
 * give us — it may occasionally be wrong (VELYS-1340 risk note).
 */
export type PasskeyDeviceType = "mobile" | "desktop" | "securityKey";

export function inferPasskeyDeviceType(cred: {
  FriendlyCredentialName?: string;
  AuthenticatorAttachment?: string;
  AuthenticatorTransports?: string[];
}): PasskeyDeviceType {
  const name = (cred.FriendlyCredentialName ?? "").toLowerCase();
  const transports = cred.AuthenticatorTransports ?? [];

  // Cross-platform authenticators reached over usb/nfc/ble are security keys.
  if (
    cred.AuthenticatorAttachment === "cross-platform" ||
    transports.some((t) => ["usb", "nfc", "ble"].includes(t))
  ) {
    return "securityKey";
  }

  const mobileKeywords = [
    "iphone",
    "ipad",
    "android",
    "pixel",
    "galaxy",
    "mobile",
  ];
  if (mobileKeywords.some((kw) => name.includes(kw))) {
    return "mobile";
  }

  // Platform authenticators default to desktop unless the name says otherwise.
  return "desktop";
}

export const getOverdueDays = (
  delta: string,
  surgeryDate: string,
  performance_window?: IAssessment["performance_window"],
): number => {
  if (!surgeryDate) return 0;

  const dueDate = getDueDate(
    moment(surgeryDate),
    performance_window && performance_window.days_post
      ? performance_window.days_post
      : delta,
  );
  const today = moment();

  const diff = today.diff(dueDate, "days");
  return diff > 0 ? diff : 0;
};

export function getCookie(name: string): string | null {
  const value = `; ${document.cookie}`;
  const parts = value.split(`; ${name}=`);
  if (parts.length === 2) {
    return parts.pop()?.split(";").shift() || null;
  }
  return null;
}

// Routes that never require auth. Defined in ./constants/publicRoutes so the
// Edge middleware (proxy.ts) can share the list without importing this module.
// Re-exported here for existing call sites.
export { PUBLIC_ROUTES };

// True when the current URL is a public route. Reads window.location so it can
// be called outside React (API interceptors) as well as in components.
export const isPublicRoute = (pathname?: string): boolean => {
  const path =
    pathname ?? (typeof window !== "undefined" ? window.location.pathname : "");
  return PUBLIC_ROUTES.some((route) => path.includes(route));
};

// Resolves a locale for client-side redirects on paths that live outside
// [locale] (e.g. /auth/callback), mirroring proxy.ts precedence. Unlike the
// authenticated success path this must not wait on geo lookup — error routing
// has to work regardless. Every candidate is validated because
// getBrowserLanguage() passes any hyphenated input straight through.
export const resolveClientLocale = (): string => {
  if (typeof window === "undefined") return DEFAULT_LOCALE;

  const preferred = getCookie("user-locale-preference");
  if (isEnabledLocale(preferred)) return preferred as string;

  const browserLocale = navigator.language;
  if (isEnabledLocale(browserLocale)) return browserLocale;

  const matched = getBrowserLanguage(browserLocale?.split("-")[0]);
  if (isEnabledLocale(matched)) return matched as string;

  return DEFAULT_LOCALE;
};

// Helper function to delete cookie
export function deleteCookie(name: string) {
  document.cookie = `${name}=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT; SameSite=Lax`;
}

export const validatePredefinedAction = (
  action: PredefinedAction,
  sendReminder: boolean,
  t: PredefinedActionValidationTranslations,
): { isValid: boolean; errors: string[] } => {
  const errors: string[] = [];

  // Required: title
  if (!action?.title?.trim()) {
    errors.push(t.titleRequired);
  }

  // Required: delta_days_from_surgery
  if (
    !action?.delta_days_from_surgery &&
    action?.delta_days_from_surgery !== "0"
  ) {
    errors.push(t.daysPrePostRequired);
  }

  // Reminder validation (only if sendReminder is enabled)
  if (sendReminder) {
    if (action?.reminders && action.reminders.length > 0) {
      action.reminders.forEach((reminder: Reminder, index: number) => {
        // Validate execution_date (time of day)
        if (!reminder.execution_date?.trim()) {
          errors.push(t.reminderTimeRequired(index + 1));
        }

        // Validate days_before_due_to_remind
        if (
          reminder.days_before_due_to_remind === undefined ||
          reminder.days_before_due_to_remind === null ||
          reminder.days_before_due_to_remind < 0
        ) {
          errors.push(t.reminderDaysRequired(index + 1));
        }

        // Validate message
        if (!reminder.message?.trim()) {
          errors.push(t.reminderMessageRequired(index + 1));
        }
      });
    }
  }

  return {
    isValid: errors.length === 0,
    errors,
  };
};

export const isValidPhoneNumber = (phoneNumber: string): boolean => {
  if (!phoneNumber) return false;

  // Remove all non-digit characters (spaces, dashes, parentheses, etc.)
  const digitsOnly = phoneNumber.replace(/\D/g, "");

  // Valid phone number should be 7-15 digits (excluding country code)
  return digitsOnly.length >= 7 && digitsOnly.length <= 15;
};

export const parsePhoneNumberWithLocale = (
  phoneNumber: string | null | undefined,
): {
  countryCode: string | null;
  locale: ILocale | null;
} => {
  if (!phoneNumber) {
    return {
      countryCode: null,
      locale: null,
    };
  }

  const digits = phoneNumber.slice(1).replace(/\D/g, "");
  if (!digits) {
    return {
      countryCode: null,
      locale: null,
    };
  }

  // Get all country codes and sort by length (longest first)
  const knownCodes = Object.values(countryCallingCodes).sort(
    (a, b) => b.length - a.length,
  );

  for (const code of knownCodes) {
    if (digits.startsWith(code)) {
      // Find the locale for this country code
      const localeEntry = Object.entries(countryCallingCodes).find(
        ([, c]) => c === code,
      );

      return {
        countryCode: code,
        locale: localeEntry ? (localeEntry[0] as ILocale) : null,
      };
    }
  }

  return {
    countryCode: null,
    locale: null,
  };
};

export const hasAcceptedCountryCode = (phoneNumber: string | undefined) => {
  return !phoneNumber
    ? false
    : acceptedCountryCodes.some((code) => phoneNumber.startsWith(code));
};

export const isCarePlanTemplate = (
  data: unknown,
): data is ICarePlanTemplate => {
  if (typeof data !== "object" || data === null) return false;

  const d = data as Record<string, unknown>;

  return (
    typeof d.care_plan_name === "string" &&
    typeof d.surgery_date === "string" &&
    typeof d.email === "string" &&
    typeof d.timezone === "string" &&
    Array.isArray(d.actions) &&
    Array.isArray(d.assessments) &&
    Array.isArray(d.broadcasts) &&
    Array.isArray(d.assessment_plans) &&
    Array.isArray(d.events)
  );
};

export const getLicenseLabels = (
  licenses: Record<string, unknown> | string[] | null | undefined,
): string[] => {
  if (!licenses) return [];

  const LICENSE_DISPLAY_NAMES: Record<string, string> = {
    "jnj.velys.patientpath": "Patient Path",
    "jnj.velys.insights": "Insights",
    "jnj.velys.patientpath.custom": "Custom",
    "jnj.velys.insights.promptai": "Amazon Q",
  };

  if (Array.isArray(licenses)) {
    return licenses
      .map((key) => LICENSE_DISPLAY_NAMES[key])
      .filter((label): label is string => label !== undefined);
  }

  return Object.entries(licenses)
    .filter(([, val]) => val === true)
    .map(([key]) => LICENSE_DISPLAY_NAMES[key])
    .filter((label): label is string => label !== undefined);
};

export const formatPhoneDisplay = (phone?: string): string => {
  if (!phone) return "";

  const { countryCode } = parsePhoneNumberWithLocale(phone);
  const digits = phone.replace(/\D/g, "");

  if (!countryCode) return phone;

  const local = digits.slice(countryCode.length);

  switch (countryCode) {
    case "1": // US/CA: +1 (555) 765-4321
      return `+${countryCode} (${local.slice(0, 3)}) ${local.slice(3, 6)}-${local.slice(6)}`;

    case "44": // UK (en-GB): +44 7911 123456
      return `+${countryCode} ${local.slice(0, 4)} ${local.slice(4)}`;

    case "43": // AT (en-AT): +43 676 1234567
      return `+${countryCode} ${local.slice(0, 3)} ${local.slice(3)}`;

    case "41": // CH (en-CH): +41 79 123 45 67
      return `+${countryCode} ${local.slice(0, 2)} ${local.slice(2, 5)} ${local.slice(5, 7)} ${local.slice(7)}`;

    case "61": // AU: +61 4XX XXX XXX
      return `+${countryCode} ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;

    case "33": // FR: +33 X XX XX XX XX
      return `+${countryCode} ${local.slice(0, 1)} ${local.slice(1, 3)} ${local.slice(3, 5)} ${local.slice(5, 7)} ${local.slice(7)}`;

    case "49": // DE: +49 XXX XXXXXXX
      return `+${countryCode} ${local.slice(0, 3)} ${local.slice(3)}`;

    case "34": // ES: +34 XXX XXX XXX
      return `+${countryCode} ${local.slice(0, 3)} ${local.slice(3, 6)} ${local.slice(6)}`;

    case "81": // JP: +81 X XXXX XXXX
      return `+${countryCode} ${local.slice(0, 1)} ${local.slice(1, 5)} ${local.slice(5)}`;

    default:
      return `+${countryCode} ${local}`;
  }
};

export function parseJsonField(raw: unknown): Record<string, unknown> {
  if (!raw) return {};
  if (typeof raw === "string") {
    try {
      return JSON.parse(raw) as Record<string, unknown>;
    } catch {
      return {};
    }
  }
  return raw as Record<string, unknown>;
}

export function stripCountryCode(phone: string): string {
  const allCodes = Object.values(countryCallingCodes).sort(
    (a, b) => b.length - a.length,
  );
  for (const code of allCodes) {
    const regex = new RegExp(`^\\+?${code}`);
    if (regex.test(phone)) return phone.replace(regex, "").trim();
  }
  return phone;
}

export const buildCalendarDays = (
  year: number,
  month: number,
): (number | null)[] => {
  const firstDay = new Date(year, month, 1).getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const grid: (number | null)[] = [];

  for (let i = 0; i < firstDay; i++) grid.push(null);
  for (let d = 1; d <= daysInMonth; d++) grid.push(d);
  while (grid.length % 7 !== 0) grid.push(null);

  return grid;
};

export const formatDisplay = (value: string, monthNames: string[]): string => {
  if (!value) return "";
  const [y, m, d] = value.split("-").map(Number);
  if (!y || !m || !d) return value;
  return `${monthNames[m - 1]} ${d}, ${y}`;
};

export const toISO = (year: number, month: number, day: number): string => {
  return `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
};

export const parseValue = (
  value: string,
): { y: number; m: number; d: number } | null => {
  if (!value) return null;
  const parts = value.split("-").map(Number);
  if (parts.length !== 3 || parts.some(isNaN)) return null;
  return { y: parts[0], m: parts[1] - 1, d: parts[2] };
};

//#region graph functions

export const toWorkflowNum = (v: unknown): number | null => {
  const n = parseFloat(v as string);
  return isNaN(n) ? null : n;
};

export const workflowAvg = (vals: number[]): number => {
  if (!vals.length) return 0;
  return (
    Math.round((vals.reduce((s, v) => s + v, 0) / vals.length) * 100) / 100
  );
};

export const toDropdownOptions = (values: string[]) =>
  values.map((v) => ({ value: v, name: v }));

export const getWorkflowDateField = (
  rows: WorkflowRow[],
  type: "TKA" | "UKA",
): string => {
  if (!rows[0]) return type === "TKA" ? "procedure date" : "treatment date";
  if ("procedure date" in rows[0]) return "procedure date";
  if ("procedure_date" in rows[0]) return "procedure_date";
  if ("surgery_date" in rows[0]) return "surgery_date";
  if ("treatment date" in rows[0]) return "treatment date";
  return "procedure date";
};

export const getWorkflowLocationField = (rows: WorkflowRow[]): string => {
  if (!rows[0]) return "surgical location";
  if ("surgical location" in rows[0]) return "surgical location";
  if ("location_name" in rows[0]) return "location_name";
  return "location";
};

export const getWorkflowClassificationField = (
  rows: WorkflowRow[],
  procedureType: "TKA" | "UKA",
): string => {
  if (procedureType !== "UKA") return "workflow classification";
  if (!rows[0]) return "bodypart";
  if ("bodypart" in rows[0]) return "bodypart";
  return "workflow classification";
};

export const formatHeader = (key: string): string =>
  key.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());

export const formatCell = (value: string | null, locale = "en-US"): string => {
  if (value === null || value === "") return "—";
  const num = parseFloat(value);
  if (!isNaN(num) && value.includes("."))
    return formatNumberForLocale(num, locale);
  return value;
};

export const avgValues = (vals: number[]): number | null =>
  vals.length ? vals.reduce((s, v) => s + v, 0) / vals.length : null;

const wrapAutolink = (value: string): string => `<${value}>`;

const wrapTelLink = (display: string): string => {
  const digits = display.replace(/\D/g, "");
  const tel = digits.length === 10 ? `+1${digits}` : `+${digits}`;
  return `[${display}](tel:${tel})`;
};
//#endregion

//#region markdown backward compatability functions
const BARE_URL_PATTERN = /(?<![(<\[])\bhttps?:\/\/[^\s<>()[\]"']+/g;
export const autoLinkBareUrls = (markdown: string): string =>
  markdown ? markdown.replace(BARE_URL_PATTERN, wrapAutolink) : markdown;

const BARE_EMAIL_PATTERN =
  /(?<![(<\[:\w.-])[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}\b/g;
export const autoLinkBareEmails = (markdown: string): string =>
  markdown ? markdown.replace(BARE_EMAIL_PATTERN, wrapAutolink) : markdown;

const BARE_PHONE_PATTERN =
  /(?<![(<\[:\w])(?:\+\d{10,15}|(?:\+1[-.\s]?)?(?:\(\d{3}\)|\d{3})[-.\s]\d{3}[-.\s]\d{4})\b/g;
export const autoLinkBarePhones = (markdown: string): string =>
  markdown ? markdown.replace(BARE_PHONE_PATTERN, wrapTelLink) : markdown;

export const autoLinkBareContent = (markdown: string): string =>
  autoLinkBarePhones(autoLinkBareEmails(autoLinkBareUrls(markdown ?? "")));

// needed for the card display from markdown
export const markdownToPlainText = (markdown: string): string => {
  if (!markdown) return "";
  return markdown
    .replace(/!\[[^\]]*]\([^)]*\)/g, "") // images → drop
    .replace(/\[([^\]]*)]\([^)]*\)/g, "$1") // [text](url) → text
    .replace(/<((?:https?|tel|mailto):[^\s>]+)>/g, "$1") // <scheme:...> → uri
    .replace(/<([^\s>@]+@[^\s>]+)>/g, "$1") // <email> → email
    .replace(/<!--[\s\S]*?-->/g, "") // HTML comments
    .replace(/<\/?[a-zA-Z][^>]*>/g, "") // any remaining HTML tag → drop, keep inner text
    .replace(/&nbsp;/gi, " ")
    .replace(/&amp;/gi, "&")
    .replace(/&lt;/gi, "<")
    .replace(/&gt;/gi, ">")
    .replace(/&quot;/gi, '"')
    .replace(/&#39;|&apos;/gi, "'")
    .replace(/`([^`]*)`/g, "$1") // `code` → code
    .replace(/[*_~]+/g, "") // **bold** / *italic* / ~strike~ markers
    .replace(/^#{1,6}\s+/gm, "") // # heading markers
    .replace(/^>\s?/gm, "") // > blockquote markers
    .replace(/^[-*+]\s+/gm, "") // - bullets
    .replace(/^\d+\.\s+/gm, "") // 1. ordered list markers
    .replace(/^[=-]{3,}\s*$/gm, "") // setext heading underlines / horizontal rules
    .replace(/\s+/g, " ") // collapse all whitespace
    .trim();
};
//#endregion

//#region asset helpers
const ASSET_MARKDOWN_TYPES = ["message", "markdown", "text"];
const ASSET_IMAGE_TYPES = [
  "image",
  "png",
  "jpg",
  "jpeg",
  "heic",
  "gif",
  "webp",
];
const ASSET_VIDEO_TYPES = ["video", "video_asset", "mp4", "mov", "webm"];
const ASSET_PDF_TYPES = ["pdf", "application/pdf"];

const ASSET_IMAGE_EXTS = ["png", "jpg", "jpeg", "heic", "gif", "webp"];
const ASSET_VIDEO_EXTS = ["mp4", "mov", "webm", "m4v", "ogg"];
const ASSET_PDF_EXTS = ["pdf"];

export type InlineAssetKind = "markdown" | "image" | "video" | "pdf";

function extractExtension(urlOrFilename?: string): string | null {
  if (!urlOrFilename) return null;
  const path = urlOrFilename.split(/[?#]/)[0];
  const match = path.match(/\.([a-z0-9]+)$/i);
  return match ? match[1].toLowerCase() : null;
}

export function getInlineAssetKind(
  assetType?: string,
  urlOrFilename?: string,
): InlineAssetKind | null {
  if (assetType) {
    const t = assetType.toLowerCase();
    if (ASSET_MARKDOWN_TYPES.includes(t)) return "markdown";
    if (ASSET_IMAGE_TYPES.includes(t)) return "image";
    if (ASSET_VIDEO_TYPES.includes(t)) return "video";
    if (ASSET_PDF_TYPES.includes(t)) return "pdf";
  }
  const ext = extractExtension(urlOrFilename);
  if (ext) {
    if (ASSET_PDF_EXTS.includes(ext)) return "pdf";
    if (ASSET_IMAGE_EXTS.includes(ext)) return "image";
    if (ASSET_VIDEO_EXTS.includes(ext)) return "video";
  }
  return null;
}

export const isInlineAsset = (
  assetType?: string,
  urlOrFilename?: string,
): boolean => getInlineAssetKind(assetType, urlOrFilename) !== null;

export function isRenderableAsset(asset: IAsset | undefined | null): boolean {
  if (!asset) return false;
  return !!(
    asset.asset_type ||
    asset.url ||
    asset.description ||
    asset.name ||
    asset.id ||
    asset.asset_id
  );
}
//#endregion

//#region asset card helpers
export type AssetTypeKey =
  | "markdown"
  | "video"
  | "pdf"
  | "image"
  | "document"
  | "other";

export const ASSET_TYPE_ORDER: AssetTypeKey[] = [
  "markdown",
  "video",
  "pdf",
  "image",
  "document",
  "other",
];

export const ACCEPT_BY_ASSET_TYPE: Record<string, Record<string, string[]>> = {
  video: { "video/mp4": [".mp4"] },
  pdf: { "application/pdf": [".pdf"] },
  image: { "image/*": [".jpeg", ".jpg", ".png"] },
  document: {
    "application/msword": [".doc"],
    "application/vnd.openxmlformats-officedocument.wordprocessingml.document": [
      ".docx",
    ],
  },
};

export const normalizeAssetType = (
  raw: string | undefined,
): AssetTypeKey | null => {
  const t = (raw ?? "").toLowerCase();
  if (t === "markdown" || t === "message") return "markdown";
  if (t === "video" || t === "video_asset") return "video";
  if (t === "image" || t === "png" || t === "jpg" || t === "jpeg")
    return "image";
  if (t === "document" || t === "docx" || t === "doc" || t === "pdf")
    return "document";
  if (t === "other" || t === "file") return "document";
  return null;
};

export const getAssetTypeIcon = (type: AssetTypeKey | null) => {
  if (type === "image") return { icon: ImageAssetIcon, color: "#A16207" };
  if (type === "markdown") return { icon: MarkdownAssetIcon, color: "#009343" };
  if (type === "video") return { icon: VideoAssetIcon, color: "#D33119" };
  return { icon: FileIcon, color: "#1478B4" };
};

export const getAssetTypeLabel = (
  t: (key: string) => string,
  type: AssetTypeKey | null,
): string => {
  if (!type) return "";
  if (type === "markdown") return t("message");
  if (type === "video") return t("video");
  if (type === "pdf") return t("PDF");
  if (type === "image") return t("image");
  if (type === "document") return t("document");
  return t("other");
};

export const translateAssetType = (
  rawType: string | undefined,
  t: (key: string) => string,
): string => {
  switch (rawType) {
    case "MARKDOWN":
      return t("message");
    case "VIDEO":
      return t("video");
    case "PDF":
      return t("PDF");
    case "IMAGE":
      return t("image");
    case "DOCUMENT":
      return t("document");
    case "OTHER":
      return t("other");
    default:
      return rawType ?? "";
  }
};
//#endregion
